import React, { useState, useRef } from "react";
import {
  GalleryContainer,
  ImageContainer,
  Image,
  ArrowButton,
  CirclesContainer,
  Circle,
} from "./styles";
import { SingleGalleryProps } from "./types";
import { IconButton } from "@mui/material";
import { ArrowBackIosNew, ArrowForwardIos, Star } from "@mui/icons-material";
import { getFavouritesList } from "../../services/favourites/getFavourites";
import Spinner from "../Spinner/Spinner";
import { postFavourite } from "../../services/favourites/postFavourites";
import { deleteFavourite } from "../../services/favourites/deleteFavourites";

const SingleGallery: React.FC<SingleGalleryProps> = ({
  resourceId,
  images,
  width = "320px",
  maxWidth,
  height = "320px",
  favorite = false,
}) => {
  // ------------------- JWT -------------------
  let jwt = process.env.REACT_APP_JWT;
  if (!jwt) {
    jwt = "";
  }

  // ------------------- STATES -------------------
  const [loadingFavourites, setLoadingFavourites] = React.useState(false); // Loading state
  const [favouritesList, setFavouritesList] = React.useState<any>([]); // Favourites list state
  const [serverResponse, setServerResponse] = React.useState(false); // Server response state
  const [currentIndex, setCurrentIndex] = useState(0);
  const [starColor, setStarColor] = useState("#D0CCC6");
  const [isFav, setIsFav] = useState(false);
  const touchStartXRef = useRef(0);
  const touchEndXRef = useRef(0);

  // ------------------- USE EFFECT -------------------
  React.useEffect(() => {
    setLoadingFavourites(true);
    getFavourites();
  }, []);

  React.useEffect(() => {
    setStarColor(!isFav ? "#D0CCC6" : "#F2A007");
  }, [isFav]);

  // ------------------- GET RESOURCES FROM API -------------------
  const getFavourites = async () => {
    await getFavouritesList(jwt!)
      .then((res) => {
        if (res && res.data) {
          setFavouritesList(res.data.favourites_list);
          res.data.favourites_list.find((fav: any) => fav.id === resourceId)
            ? setIsFav(true)
            : setIsFav(false);
        }
      })
      .catch((err) => {});

    setLoadingFavourites(false);
  };

  const postFavouriteById = async (token: string, id: number) => {
    await postFavourite(token, id)
      .then((res) => {
        if (res && res.data) {
          if (res.status !== 200) {
            setServerResponse(false);
          } else {
            setServerResponse(true);
          }
        }
      })
      .catch((err) => {
        setServerResponse(true);
      });
  };

  const deleteFavouriteById = async (token: string, id: number) => {
    await deleteFavourite(token, id)
      .then((res) => {
        if (res && res.data) {
          if (res.status !== 200) {
            setServerResponse(false);
          } else {
            setServerResponse(true);
          }
        }
      })
      .catch((err) => {
        setServerResponse(true);
      });
  };

  // ------------------- HANDLE FUNCTIONS -------------------
  const handlePrevClick = (e?: React.MouseEvent<HTMLButtonElement>) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? images.length - 1 : prevIndex - 1
    );
  };

  const handleNextClick = (e?: React.MouseEvent<HTMLButtonElement>) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    setCurrentIndex((prevIndex) =>
      prevIndex === images.length - 1 ? 0 : prevIndex + 1
    );
  };

  const handleTouchStart = (event: React.TouchEvent<HTMLDivElement>) => {
    touchStartXRef.current = event.touches[0].clientX;
  };

  const handleTouchMove = (event: React.TouchEvent<HTMLDivElement>) => {
    touchEndXRef.current = event.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    const touchDeltaX = touchEndXRef.current - touchStartXRef.current;

    if (touchDeltaX > 50) {
      handlePrevClick();
    } else if (touchDeltaX < -50) {
      handleNextClick();
    }

    touchStartXRef.current = 0;
    touchEndXRef.current = 0;
  };

  const handleStarClick = (e?: React.MouseEvent<HTMLButtonElement>) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    if (!isFav) {
      postFavouriteById(jwt!, resourceId!);
    } else {
      deleteFavouriteById(jwt!, resourceId!);
    }
    setIsFav(!isFav);
  };

  const loadingImages = [
    "https://pa1.aminoapps.com/6707/510b0daee67fbc091f14b9d8ef40aeb6c0d4dc7d_hq.gif",
    "https://pa1.aminoapps.com/6707/510b0daee67fbc091f14b9d8ef40aeb6c0d4dc7d_hq.gif",
    "https://pa1.aminoapps.com/6707/510b0daee67fbc091f14b9d8ef40aeb6c0d4dc7d_hq.gif",
    "https://pa1.aminoapps.com/6707/510b0daee67fbc091f14b9d8ef40aeb6c0d4dc7d_hq.gif",
    "https://pa1.aminoapps.com/6707/510b0daee67fbc091f14b9d8ef40aeb6c0d4dc7d_hq.gif",
  ];

  return (
    <>
      {!loadingFavourites ? (
        <GalleryContainer height={height} width={width} maxWidth={maxWidth}>
          {favorite && (
            <IconButton
              sx={{
                position: "absolute",
                top: 10,
                right: 10,
                zIndex: 1,
                opacity: 0.8,
                transition:
                  "opacity 0.3s ease-in-out, transform 0.3s ease-in-out",
                "&:hover": {
                  opacity: 1,
                  transform: "scale(1.05)",
                },
              }}
              onClick={handleStarClick}
            >
              <Star style={{ color: starColor, fontSize: 35 }} />
            </IconButton>
          )}
          <ArrowButton onClick={handlePrevClick} left>
            <IconButton
              sx={{
                background: "white",
                opacity: 0.8,
                transition:
                  "opacity 0.3s ease-in-out, transform 0.3s ease-in-out",
                "&:hover": {
                  background: "white",
                  opacity: 1,
                  transform: "scale(1.05)",
                },
              }}
            >
              <ArrowBackIosNew style={{ color: "black", fontSize: 15 }} />
            </IconButton>
          </ArrowButton>
          <ImageContainer
            height={height}
            width={width}
            maxWidth={maxWidth}
            style={{ transform: `translateX(-${currentIndex * 100}%)` }}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            {images.length > 0
              ? images.map((image, index) => (
                  <Image
                    key={index}
                    src={image}
                    alt={`Image ${index + 1}`}
                    width={width}
                    height={height}
                  />
                ))
              : loadingImages.map((image, index) => (
                  <Image
                    key={index}
                    src={image}
                    alt={`Loading Image ${index + 1}`}
                    width={width}
                    height={height}
                  />
                ))}
          </ImageContainer>
          <ArrowButton onClick={handleNextClick} right>
            <IconButton
              sx={{
                background: "white",
                opacity: 0.8,
                transition:
                  "opacity 0.3s ease-in-out, transform 0.3s ease-in-out",
                "&:hover": {
                  background: "white",
                  opacity: 1,
                  transform: "scale(1.05)",
                },
              }}
            >
              <ArrowForwardIos sx={{ color: "black", fontSize: 15 }} />
            </IconButton>
          </ArrowButton>
          <CirclesContainer>
            {images.map((_, index) => (
              <Circle
                key={index}
                active={index === currentIndex}
                style={{ fontSize: 9 }}
              />
            ))}
          </CirclesContainer>
        </GalleryContainer>
      ) : (
        <Spinner />
      )}
    </>
  );
};

export default SingleGallery;
